// Error handling utilities for database operations

/**
 * Handles Supabase/PostgreSQL errors with user-friendly messages
 * @param {Error} error - The error object from Supabase
 * @param {string} operation - Description of the operation that failed
 * @returns {Error} - Formatted error with user-friendly message
 */
export function handleDatabaseError(error, operation = 'database operation') {
  console.error(`Database error during ${operation}:`, error)
  
  // Handle specific PostgreSQL error codes
  if (error.code) {
    switch (error.code) {
      case '42P01': // relation does not exist
        return new Error(
          `Database tables are not set up properly. Please run the database setup script. ` +
          `See DATABASE_SETUP_GUIDE.md for instructions.`
        )
      
      case '23503': // foreign key violation
        return new Error(
          `Database relationship error. This may indicate missing required data or ` +
          `incorrect table relationships. Please check the database setup.`
        )
      
      case '23505': // unique violation
        return new Error(
          `This record already exists. Please check for duplicates.`
        )
      
      case '42710': // duplicate object (trigger, policy, etc.)
        return new Error(
          `Database object already exists. This is usually resolved by running ` +
          `the setup script which includes DROP IF EXISTS statements.`
        )
      
      case 'PGRST200': // PostgREST relationship error
        return new Error(
          `Database relationship not found. This indicates missing foreign key ` +
          `relationships. Please ensure the database setup script has been executed.`
        )
      
      default:
        return new Error(
          `Database error (${error.code}): ${error.message}. ` +
          `If this persists, please check the database setup.`
        )
    }
  }
  
  // Handle network/connection errors
  if (error.message?.includes('fetch') || error.message?.includes('network')) {
    return new Error(
      `Unable to connect to the database. Please check your internet connection ` +
      `and Supabase configuration.`
    )
  }
  
  // Handle authentication errors
  if (error.message?.includes('JWT') || error.message?.includes('auth')) {
    return new Error(
      `Authentication error. Please log in again.`
    )
  }
  
  // Default error handling
  return new Error(
    `${operation} failed: ${error.message}. ` +
    `If this error persists, please check the database setup guide.`
  )
}

/**
 * Wraps a database operation with error handling
 * @param {Function} operation - The async database operation to execute
 * @param {string} operationName - Description of the operation
 * @returns {Promise} - Result of the operation or handled error
 */
export async function withErrorHandling(operation, operationName) {
  try {
    return await operation()
  } catch (error) {
    throw handleDatabaseError(error, operationName)
  }
}

/**
 * Checks if an error is related to missing database setup
 * @param {Error} error - The error to check
 * @returns {boolean} - True if error indicates missing database setup
 */
export function isDatabaseSetupError(error) {
  const setupErrorCodes = ['42P01', 'PGRST200']
  const setupErrorMessages = [
    'relation does not exist',
    'foreign key',
    'table does not exist',
    'relationship not found'
  ]
  
  if (error.code && setupErrorCodes.includes(error.code)) {
    return true
  }
  
  const message = error.message?.toLowerCase() || ''
  return setupErrorMessages.some(msg => message.includes(msg))
}

/**
 * Creates a fallback response when database operations fail
 * @param {string} dataType - Type of data being requested (e.g., 'courses', 'quizzes')
 * @returns {Object} - Fallback response object
 */
export function createFallbackResponse(dataType) {
  return {
    data: [],
    count: 0,
    error: null,
    message: `${dataType} data unavailable - database not configured`,
    fallback: true
  }
}

/**
 * Logs database setup instructions to console
 */
export function logDatabaseSetupInstructions() {
  console.group('🔧 Database Setup Required')
  console.log('The database tables are not properly configured.')
  console.log('Please follow these steps:')
  console.log('1. Open your Supabase dashboard')
  console.log('2. Navigate to the SQL Editor')
  console.log('3. Execute the database/setup_learning_center.sql script')
  console.log('4. Refresh this page')
  console.log('')
  console.log('For detailed instructions, see: DATABASE_SETUP_GUIDE.md')
  console.groupEnd()
}